import React, { useState } from 'react';
import { ArrowLeft, Save, Send, Plus, X, Calendar, DollarSign, MapPin } from 'lucide-react';
import { createJobOpportunity } from '../../services/jobService';
import { Link, useNavigate } from 'react-router-dom';

const PostJob = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    job_type: 'full_time',
    category: 'technology',
    experience_level: 'entry_level',
    location: '',
    is_remote: false,
    salary_min: '',
    salary_max: '',
    currency: 'USD',
    requirements: [''],
    responsibilities: [''],
    qualifications: [''],
    benefits: [''],
    skills_required: [''],
    application_deadline: '',
    start_date: '',
    application_url: '',
    contact_email: '',
    tags: '',
    status: 'draft',
  });

  const jobTypes = [
    { value: 'full_time', label: 'Full Time' },
    { value: 'part_time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'internship', label: 'Internship' },
    { value: 'apprenticeship', label: 'Apprenticeship' },
    { value: 'volunteer', label: 'Volunteer' },
  ];

  const experienceLevels = [
    { value: 'entry_level', label: 'Entry Level' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid_level', label: 'Mid Level' },
    { value: 'senior_level', label: 'Senior Level' },
    { value: 'executive', label: 'Executive' },
    { value: 'student', label: 'Student' },
  ];

  const categories = [
    { value: 'technology', label: 'Technology' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'energy', label: 'Energy' },
    { value: 'water_management', label: 'Water Management' },
    { value: 'waste_management', label: 'Waste Management' },
    { value: 'governance', label: 'Governance' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'safety_security', label: 'Safety & Security' },
    { value: 'economic_development', label: 'Economic Development' },
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayFieldChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev?.[field]?.map((item, i) => i === index ? value : item) || []
    }));
  };

  const addArrayField = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...(prev?.[field] || []), '']
    }));
  };

  const removeArrayField = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev?.[field]?.filter((_, i) => i !== index) || []
    }));
  };

  const handleSubmit = async (e, statusOverride = null) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const cleanedData = {
        ...formData,
        salary_min: formData?.salary_min ? parseFloat(formData?.salary_min) : null,
        salary_max: formData?.salary_max ? parseFloat(formData?.salary_max) : null,
        requirements: formData?.requirements?.filter(item => item?.trim()) || [],
        responsibilities: formData?.responsibilities?.filter(item => item?.trim()) || [],
        qualifications: formData?.qualifications?.filter(item => item?.trim()) || [],
        benefits: formData?.benefits?.filter(item => item?.trim()) || [],
        skills_required: formData?.skills_required?.filter(item => item?.trim()) || [],
        tags: formData?.tags ? formData?.tags?.split(',')?.map(tag => tag?.trim())?.filter(Boolean) : [],
        application_deadline: formData?.application_deadline || null,
        start_date: formData?.start_date || null,
        application_url: formData?.application_url || null,
        contact_email: formData?.contact_email || null,
        status: statusOverride || formData?.status,
      };

      await createJobOpportunity(cleanedData);
      setSuccess(true);
      
      setTimeout(() => {
        navigate('/job-opportunities');
      }, 2000);
    } catch (err) {
      setError(err?.message || 'Failed to create job posting');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/job-opportunities"
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Post a Job</h1>
                <p className="text-gray-600">Create a new job opportunity</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
            <h3 className="font-medium">Job Posted Successfully!</h3>
            <p className="text-sm mt-1">Redirecting to job listings...</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <h3 className="font-medium">Error</h3>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border p-8">
          <div className="space-y-8">
            {/* Basic Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Basic Information</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Title *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData?.title}
                    onChange={(e) => handleInputChange('title', e?.target?.value)}
                    placeholder="e.g. Smart Cities Data Analyst"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Job Type *
                  </label>
                  <select
                    value={formData?.job_type}
                    onChange={(e) => handleInputChange('job_type', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {jobTypes?.map(type => (
                      <option key={type?.value} value={type?.value}>{type?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData?.category}
                    onChange={(e) => handleInputChange('category', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categories?.map(category => (
                      <option key={category?.value} value={category?.value}>{category?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level *
                  </label>
                  <select
                    value={formData?.experience_level}
                    onChange={(e) => handleInputChange('experience_level', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {experienceLevels?.map(level => (
                      <option key={level?.value} value={level?.value}>{level?.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      required
                      value={formData?.location}
                      onChange={(e) => handleInputChange('location', e?.target?.value)}
                      placeholder="e.g. San Francisco, CA"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_remote"
                      checked={formData?.is_remote}
                      onChange={(e) => handleInputChange('is_remote', e?.target?.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="is_remote" className="ml-2 text-sm text-gray-700">
                      This position offers remote work options
                    </label>
                  </div>
                </div>
              </div>
            </section>

            {/* Salary Information */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Salary Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Salary
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      value={formData?.salary_min}
                      onChange={(e) => handleInputChange('salary_min', e?.target?.value)}
                      placeholder="50000"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maximum Salary
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="number"
                      value={formData?.salary_max}
                      onChange={(e) => handleInputChange('salary_max', e?.target?.value)}
                      placeholder="80000"
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency
                  </label>
                  <select
                    value={formData?.currency}
                    onChange={(e) => handleInputChange('currency', e?.target?.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>
            </section>

            {/* Job Description */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Job Description</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Describe the job role, company culture, and what makes this opportunity exciting..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                />
              </div>
            </section>

            {/* Job Details */}
            {['responsibilities', 'requirements', 'qualifications', 'benefits', 'skills_required']?.map((field) => (
              <section key={field}>
                <h2 className="text-xl font-semibold text-gray-900 mb-6 capitalize">
                  {field?.replace('_', ' ')}
                </h2>
                <div className="space-y-3">
                  {(formData?.[field] || [''])?.map((item, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={item}
                        onChange={(e) => handleArrayFieldChange(field, index, e?.target?.value)}
                        placeholder={`Enter ${field?.slice(0, -1)?.replace('_', ' ')}...`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => removeArrayField(field, index)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addArrayField(field)}
                    className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  >
                    <Plus className="w-4 h-4" />
                    Add {field?.slice(0, -1)?.replace('_', ' ')}
                  </button>
                </div>
              </section>
            ))}

            {/* Application Details */}
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Application Details</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application Deadline
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="datetime-local"
                      value={formData?.application_deadline}
                      onChange={(e) => handleInputChange('application_deadline', e?.target?.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Start Date
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="date"
                      value={formData?.start_date}
                      onChange={(e) => handleInputChange('start_date', e?.target?.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Application URL
                  </label>
                  <input
                    type="url"
                    value={formData?.application_url}
                    onChange={(e) => handleInputChange('application_url', e?.target?.value)}
                    placeholder="https://company.com/apply"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave blank to use built-in application system
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData?.contact_email}
                    onChange={(e) => handleInputChange('contact_email', e?.target?.value)}
                    placeholder="hr@company.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={formData?.tags}
                    onChange={(e) => handleInputChange('tags', e?.target?.value)}
                    placeholder="smart-cities, data-analysis, urban-planning (comma separated)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </section>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={(e) => handleSubmit(e, 'draft')}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
            >
              <Save className="w-4 h-4" />
              Save as Draft
            </button>
            
            <button
              type="button"
              onClick={(e) => handleSubmit(e, 'active')}
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Publishing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Publish Job
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostJob;